import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Button, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Paper,
  Container, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { collection, getDocs, query, where, doc, updateDoc, } from "firebase/firestore";
import { db } from "../../firebase";

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: theme.shadows[3],
  marginBottom: theme.spacing(4),
}));

const ResetBill = ({
  unitCode,
  companyCode,
  unitType,
  companyName,
  userId,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const printRef = useRef();

  const queryParams = new URLSearchParams(location.search);

  const urlUnitCode =
    queryParams.get("unit") || location.state?.unitCode || "";

  const effectiveUnitCode = urlUnitCode || unitCode;

  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [grandTotal, setGrandTotal] = useState(0);
  const [openConfirm, setOpenConfirm] = useState(false);

  useEffect(() => {
    const fetchOrders = async () => {
      if (!effectiveUnitCode) {
        setError("Unit code is missing.");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        const ordersQuery = query(
          collection(db, "orders"),
          where("unitCode", "==", effectiveUnitCode),
           where("isActive", "==", true)
        );

        const ordersSnapshot = await getDocs(ordersQuery);

        const ordersData = ordersSnapshot.docs
  .map((doc) => ({
    id: doc.id,
    ...doc.data(),
  }))
  .filter(
    (order) =>
      order.billNo !== undefined &&
      order.billNo !== null &&
      order.billNo !== ""
  );

// Sort by bill number
const sortedOrders = ordersData.sort(
  (a, b) => Number(a.billNo || 0) - Number(b.billNo || 0)
);

setOrders(sortedOrders);
const total = sortedOrders.reduce(
  (sum, order) => sum + Number(order.grandTotal  || 0),
  0
);

setGrandTotal(total);
      

        setIsLoading(false);
      } catch (err) {
        console.error(err);
        setError("Failed to load billing details.");
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, [effectiveUnitCode]);

  const handleResetBill = async () => {
    try {
      const preferenceQuery = query(
        collection(db, "preferences"),
        where("unitCode", "==", effectiveUnitCode),
        where("preferenceName", "==", "Bill No")
      );

      const preferenceSnapshot = await getDocs(preferenceQuery);

      if (!preferenceSnapshot.empty) {
        const preferenceDoc = preferenceSnapshot.docs[0];

        await updateDoc(doc(db, "preferences", preferenceDoc.id), {
          preferenceValue: 0,
        });

        const ordersQuery = query(
      collection(db, "orders"),
      where("unitCode", "==", effectiveUnitCode),
      where("isActive", "==", true)
    );

    const snapshot = await getDocs(ordersQuery);

    await Promise.all(
      snapshot.docs.map((d) =>
        updateDoc(doc(db, "orders", d.id), {
          isActive: false,
        })
      )
    );
        alert("Bill number reset successfully.");
         // Clear UI data
  setOrders([]);
  setGrandTotal(0);
      } else {
        alert("Bill No preference not found.");
      }

      setOpenConfirm(false);
    } catch (error) {
      console.error("Error resetting bill number:", error);
      alert("Failed to reset bill number.");
    }
  };

const handlePrint = () => {
  const printWindow = window.open("", "_blank");

  printWindow.document.write(`
    <html>
      <head>
        <title>Billing Details</title>

        <style>
          body {
            font-family: Arial, sans-serif;
            padding: 20px;
          }

          h2 {
            text-align: center;
            margin-bottom: 20px;
          }

          table {
            width: 100%;
            border-collapse: collapse;
          }

          table, th, td {
            border: 1px solid #000;
          }

          th, td {
            padding: 10px;
            text-align: center;
          }

          .grand-total {
            margin-top: 20px;
            text-align: right;
            font-size: 18px;
            font-weight: bold;
          }
        </style>
      </head>

      <body>
        <div class="header">
          <h2>Billing Details - ${companyName || "Unknown Company"}</h2>
        </div>

        <table>
          <tr>
            <th>Bill No</th>
            <th>Bill Date</th>
            <th>Bill Total (₹)</th>
          </tr>

          ${orders
            .map(
              (order) => `
                <tr>
                  <td>${order.billNo ?? "N/A"}</td>

                  <td>
                    ${
                      order.createdAt?.toDate()
                        ? order.createdAt
                            .toDate()
                            .toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                        : "N/A"
                    }
                  </td>

                  <td>
                    ₹ ${Number(order.grandTotal || 0).toFixed(2)}
                  </td>
                </tr>
              `
            )
            .join("")}
        </table>

        <div class="grand-total">
          Grand Total: ₹ ${grandTotal.toFixed(2)}
        </div>

        <script>
          window.onload = () => {
            window.print();
            window.onafterprint = () => window.close();
          };
        </script>
      </body>
    </html>
  `);

  printWindow.document.close();
};

  const handleBackToDashboard = () => {
    navigate("/unit_manager/home", {
      state: {
        unitCode: effectiveUnitCode,
        companyCode,
        companyName,
        unitType,
      },
    });
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading billing details...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: "center" }}>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box ref={printRef}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: "bold",
            color: "primary.main",
            mb: 4,
            textAlign: "center",
          }}
        >
          Billing Details - {companyName}
        </Typography>

        <StyledCard>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "primary.light" }}>
                  <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>
                    Bill No
                  </TableCell>

                  <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>
                    Bill Date
                  </TableCell>

                  <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>
                    Bill Total (₹)
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {orders.length > 0 ? (
                  orders.map((order, index) => (
                    <TableRow
                      key={order.id}
                      sx={{
                        bgcolor: index % 2 === 0 ? "#fafafa" : "#fff",
                      }}
                    >
                      <TableCell>{order.billNo ?? "N/A"}</TableCell>

                      <TableCell>
                        {order.createdAt?.toDate().toLocaleDateString(
                          "en-US",
                          {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          }
                        ) || "N/A"}
                      </TableCell>

                      <TableCell>
                       ₹ {Number(order.grandTotal || 0).toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No billing records found
                    </TableCell>
                  </TableRow>
                )}

                <TableRow>
                  <TableCell
                    colSpan={2}
                    align="right"
                    sx={{ fontWeight: "bold", fontSize: 16 }}
                  >
                    Grand Total
                  </TableCell>

                  <TableCell
                    sx={{ fontWeight: "bold", fontSize: 16 }}
                  >
                    ₹ {grandTotal.toFixed(2)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </StyledCard>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 2,
          mt: 3,
        }}
      >
        {orders.length > 0 && (
    <>
      <Button
        variant="contained"
        color="error"
        onClick={() => setOpenConfirm(true)}
      >
        Reset Bill
      </Button>

      <Button
        variant="contained"
        color="primary"
        onClick={handlePrint}
      >
        Print
      </Button>
    </>
  )}
        <Button
          variant="outlined"
          onClick={handleBackToDashboard}
        >
          Back
        </Button>
      </Box>

      <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
        <DialogTitle>Confirm Reset</DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to reset the bill number to 1?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenConfirm(false)}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={handleResetBill}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ResetBill;